import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { Notifications } from '../components/Notifications';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { authAPI } from '../services/api';
import './Dashboard.css';

export function Dashboard() {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const data = await authAPI.getDashboardStats();
        setDashboardData(data);
      } catch (error: any) {
        console.error('Failed to fetch dashboard data:', error);
        setError('대시보드 데이터를 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchDashboardData();
    }
  }, [user]);

  // 날짜 포맷팅 함수
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'short'
    });
  };

  // 상태 한글화 함수
  const getStatusText = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'pending': '대기중',
      'confirmed': '승인됨',
      'assigned': '배정됨',
      'in_progress': '진행중',
      'completed': '완료',
      'cancelled': '취소됨'
    };
    return statusMap[status] || status;
  };

  // 상태별 CSS 클래스
  const getStatusClass = (status: string) => {
    const statusClassMap: { [key: string]: string } = {
      'pending': 'status-pending',
      'confirmed': 'status-confirmed',
      'assigned': 'status-assigned',
      'in_progress': 'status-in-progress',
      'completed': 'status-completed',
      'cancelled': 'status-cancelled'
    };
    return statusClassMap[status] || 'status-pending';
  };

  // 요일 한글화 함수
  const getDayOfWeek = (day: number) => {
    const days = ['월', '화', '수', '목', '금', '토', '일'];
    return days[day] || '요일';
  };

  // 주기 한글화 함수
  const getFrequencyText = (frequency: string) => {
    const frequencyMap: { [key: string]: string } = {
      'weekly': '매주',
      'biweekly': '격주',
      'monthly': '매월'
    };
    return frequencyMap[frequency] || frequency;
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="loading-container">
          <LoadingSpinner />
          <p>대시보드 데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-page">
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={() => window.location.reload()}>다시 시도</button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <div className="welcome-section">
          <h1>안녕하세요, {user?.full_name || user?.username}님! 👋</h1>
          <p>SSLMO 폐기물 관리 시스템에 오신 것을 환영합니다.</p>
        </div>
        <div className="header-actions">
          {user?.role === 'admin' && (
            <div className="admin-badge">
              <span className="badge-icon">⚙️</span>
              <span>관리자</span>
            </div>
          )}
        </div>
      </div>

      <div className="dashboard-grid">
        {/* 통계 카드 섹션 */}
        <section className="stats-section">
          <h2 className="section-title">📊 요약 통계</h2>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">🗑️</div>
              <div className="stat-content">
                <h3>총 수거 횟수</h3>
                <p className="stat-number">{dashboardData?.totalCollections || 0}</p>
                <span className="stat-label">회</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">⏰</div>
              <div className="stat-content">
                <h3>예정된 수거</h3>
                <p className="stat-number">{dashboardData?.upcomingCollectionsCount || 0}</p>
                <span className="stat-label">건</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">📅</div>
              <div className="stat-content">
                <h3>이번 달 수거</h3>
                <p className="stat-number">{dashboardData?.thisMonthCollections || 0}</p>
                <span className="stat-label">회</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">⚖️</div>
              <div className="stat-content">
                <h3>총 폐기물 용량</h3>
                <p className="stat-number">{dashboardData?.totalWasteVolume || 0}</p>
                <span className="stat-label">L</span>
              </div>
            </div>
          </div>
        </section>

        {/* 빠른 작업 섹션 */}
        <section className="quick-actions-section">
          <h2 className="section-title">⚡ 빠른 작업</h2>
          <div className="quick-actions-grid">
            <Link to="/sslmo/collection" className="action-card">
              <div className="action-icon">🥬</div>
              <h3>음식물 수거 요청</h3>
              <p>음식물 폐기물 수거를 신청하세요</p>
            </Link>
            <Link to="/sslmo/history" className="action-card">
              <div className="action-icon">📋</div>
              <h3>수거 내역</h3>
              <p>지난 수거 기록을 확인하세요</p>
            </Link>
            <Link to="/sslmo/billing" className="action-card">
              <div className="action-icon">💳</div>
              <h3>결제 관리</h3>
              <p>요금 내역과 결제를 관리하세요</p>
            </Link>
            <Link to="/sslmo/analysis" className="action-card">
              <div className="action-icon">📊</div>
              <h3>수거 분석</h3>
              <p>수거 통계와 분석을 확인하세요</p>
            </Link>
          </div>
        </section>

        {/* 최근 수거 내역 */}
        <section className="recent-collections-section">
          <h2 className="section-title">🕒 최근 수거 내역</h2>
          <div className="collections-list">
            {dashboardData?.recentCollections && dashboardData.recentCollections.length > 0 ? (
              dashboardData.recentCollections.map((collection: any) => (
                <div key={collection.id} className="collection-item">
                  <div className="collection-date">{formatDate(collection.date)}</div>
                  <div className="collection-type">음식물 폐기물</div>
                  <div className="collection-volume">
                    {collection.billing_volume ? `${collection.billing_volume}L` :
                     collection.actual_volume ? `${collection.actual_volume}L` : 
                     collection.estimated_volume ? `예상: ${collection.estimated_volume}L` : '-'}
                  </div>
                  <div className={`collection-status ${getStatusClass(collection.status)}`}>
                    {getStatusText(collection.status)}
                  </div>
                  {collection.status === 'completed' && (
                    <Link to={`/sslmo/receipt/${collection.id}`} className="view-receipt-link">
                      영수증
                    </Link>
                  )}
                </div>
              ))
            ) : (
              <div className="no-collections">
                <p>아직 수거 내역이 없습니다.</p>
              </div>
            )}
          </div>
        </section>

        {/* 예정된 수거 및 정기 수거 */}
        <section className="upcoming-collections-section">
          <h2 className="section-title">📅 예정된 수거</h2>
          <div className="collections-list">
            {dashboardData?.upcomingCollections && dashboardData.upcomingCollections.length > 0 ? (
              dashboardData.upcomingCollections.map((collection: any) => (
                <div key={collection.id} className="collection-item">
                  <div className="collection-date">{formatDate(collection.date)}</div>
                  <div className="collection-time">{collection.time}</div>
                  <div className="collection-type">음식물 폐기물</div>
                  <div className="collection-volume">
                    {collection.estimated_volume ? `예상: ${collection.estimated_volume}L` : '-'}
                  </div>
                  <div className={`collection-status ${getStatusClass(collection.status)}`}>
                    {getStatusText(collection.status)}
                  </div>
                  {collection.next_collection_date && (
                    <div className="next-collection-info">
                      다음 수거: {formatDate(collection.next_collection_date)}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="no-collections">
                <p>예정된 수거가 없습니다.</p>
              </div>
            )}
          </div>
          
          {/* 정기 수거 스케줄 */}
          {dashboardData?.recurringSchedules && dashboardData.recurringSchedules.length > 0 && (
            <div className="recurring-schedules-section">
              <h3 className="subsection-title">🔄 정기 수거 스케줄</h3>
              <div className="recurring-schedules-list">
                {dashboardData.recurringSchedules.map((schedule: any) => (
                  <div key={schedule.id} className="schedule-item">
                    <div className="schedule-info">
                      <div className="schedule-description">{schedule.description}</div>
                      <div className="schedule-time">
                        {getDayOfWeek(schedule.day_of_week)} {schedule.time_of_day}
                      </div>
                      <div className="schedule-frequency">
                        {getFrequencyText(schedule.frequency)}
                      </div>
                    </div>
                    <div className={`schedule-status ${schedule.is_active ? 'active' : 'inactive'}`}>
                      {schedule.is_active ? '활성' : '비활성'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* 알림 섹션 */}
        <section className="notifications-section">
          <Notifications maxVisible={4} compact={true} />
        </section>

        {user?.role === 'admin' && (
          <section className="admin-section">
            <h2 className="section-title">⚙️ 관리자 기능</h2>
            <div className="admin-actions-grid">
              <Link to="/sslmo/admin" className="admin-action-card">
                <div className="action-icon">👥</div>
                <h3>시스템 관리</h3>
                <p>사용자 및 시스템 관리</p>
              </Link>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}